import React, { useState, useEffect } from 'react';
import { Search, Download, Edit, Trash2, Eye } from 'lucide-react';
import Swal from 'sweetalert2';
import './styles.css';

const JobApp = () => {
  const [applicants, setApplicants] = useState([]);
  const [filteredApplicants, setFilteredApplicants] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  
  // API URL - Pastikan ini PERSIS sama dengan URL yang berhasil di browser
  const API_URL = 'https://ramusen.com/process_application.php';

  useEffect(() => {
    fetchApplicants();
  }, []);

  useEffect(() => {
    filterApplicants();
  }, [searchTerm, applicants]);

  const fetchApplicants = async () => {
    try {
      setLoading(true);
      console.log('Fetching from URL:', API_URL);
      
      const response = await fetch(API_URL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      console.log('Response text:', text);

      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('JSON parse error:', e);
        throw new Error('Invalid JSON response from server');
      }

      console.log('Parsed data:', data);
      
      if (data.status === 'success') {
        console.log('Data berhasil:', data.data);
        setApplicants(data.data || []);
        setFilteredApplicants(data.data || []);
      } else {
        console.error('API returned error:', data.message);
        Swal.fire('Error', data.message || 'Failed to fetch data', 'error');
      }
    } catch (error) {
      console.error('Fetch error:', error);
      Swal.fire({
        icon: 'error',
        title: 'Connection Error',
        text: `Failed to connect to server: ${error.message}`,
        footer: `API URL: ${API_URL}`
      });
    } finally {
      setLoading(false);
    }
  };

  const filterApplicants = () => {
    if (!searchTerm) {
      setFilteredApplicants(applicants);
      return;
    }

    const filtered = applicants.filter(applicant => 
      applicant.nama?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      applicant.jenis_pekerjaan?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      applicant.no_telp?.includes(searchTerm)
    );
    setFilteredApplicants(filtered);
  };

  const handleDelete = async (id) => {
  const result = await Swal.fire({
    title: 'Are you sure?',
    text: "You won't be able to revert this!",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: 'Yes, delete it!'
  });

  if (result.isConfirmed) {
    try {
      console.log('Deleting ID:', id);
      console.log('Delete URL:', `${API_URL}?id=${id}`);
      
      const response = await fetch(`${API_URL}?id=${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      console.log('Delete response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const text = await response.text();
      console.log('Delete response text:', text);
      
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error('JSON parse error:', e);
        throw new Error('Invalid JSON response');
      }
      
      console.log('Delete response data:', data);
      
      if (data.status === 'success') {
        await Swal.fire('Deleted!', data.message, 'success');
        fetchApplicants();
      } else {
        Swal.fire('Error', data.message, 'error');
      }
    } catch (error) {
      console.error('Delete error:', error);
      Swal.fire('Error', `Failed to delete: ${error.message}`, 'error');
    }
  }
};

  const handleView = (applicant) => {
    setSelectedApplicant(applicant);
    setShowModal(true);
  };

  const downloadCV = (cvPath, nama) => {
    if (cvPath) {
      // Buat URL lengkap untuk CV
      const fullUrl = cvPath.startsWith('http') 
        ? cvPath 
        : `https://ramusen.com/${cvPath}`;
      window.open(fullUrl, '_blank');
    } else {
      Swal.fire('Info', 'CV tidak tersedia', 'info');
    }
  };

  if (loading) {
    return (
      <div className="job-app-container">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="job-app-container">
      <div className="job-app-header">
        <h1 className="job-app-title">Job Applications</h1>
        <p className="job-app-subtitle">Manage all job applicants</p>
      </div>

      {/* Search Bar */}
      <div className="search-container">
        <div className="search-box">
          <Search className="search-icon" size={20} />
          <input
            type="text"
            placeholder="Search by name, job type, or phone..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>
        <div className="stats">
          <span className="stats-text">
            Total Applicants: <strong>{filteredApplicants.length}</strong>
          </span>
        </div>
      </div>

      {/* Table */}
      <div className="table-container">
        <table className="applicants-table">
          <thead>
            <tr>
              <th>No</th>
              <th>Nama</th>
              <th>Jenis Pekerjaan</th>
              <th>No. Telepon</th>
              <th>Alamat</th>
              <th>CV</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredApplicants.length > 0 ? (
              filteredApplicants.map((applicant, index) => (
                <tr key={applicant.id}>
                  <td>{index + 1}</td>
                  <td className="name-cell">{applicant.nama}</td>
                  <td>
                    <span className="job-badge">{applicant.jenis_pekerjaan}</span>
                  </td>
                  <td>{applicant.no_telp}</td>
                  <td className="address-cell">{applicant.alamat}</td>
                  <td>
                    {applicant.cv_path ? (
                      <button
                        onClick={() => downloadCV(applicant.cv_path, applicant.nama)}
                        className="btn-download"
                        title="Download CV"
                      >
                        <Download size={16} />
                      </button>
                    ) : (
                      <span className="text-muted">-</span>
                    )}
                  </td>
                  <td>
                    <div className="action-buttons">
                      <button
                        onClick={() => handleView(applicant)}
                        className="btn-action btn-view"
                        title="View Details"
                      >
                        <Eye size={16} />
                      </button>
                      <button
                        onClick={() => handleDelete(applicant.id)}
                        className="btn-action btn-delete"
                        title="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-data">
                  {searchTerm ? 'No applicants found matching your search' : 'No applicants data available'}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {showModal && selectedApplicant && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Applicant Details</h2>
              <button 
                className="modal-close"
                onClick={() => setShowModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-row">
                <label>Nama:</label>
                <span>{selectedApplicant.nama}</span>
              </div>
              <div className="detail-row">
                <label>Jenis Pekerjaan:</label>
                <span className="job-badge">{selectedApplicant.jenis_pekerjaan}</span>
              </div>
              <div className="detail-row">
                <label>No. Telepon:</label>
                <span>{selectedApplicant.no_telp}</span>
              </div>
              <div className="detail-row">
                <label>Alamat:</label>
                <span>{selectedApplicant.alamat}</span>
              </div>
              {selectedApplicant.cv_path && (
                <div className="detail-row">
                  <label>CV:</label>
                  <button
                    onClick={() => downloadCV(selectedApplicant.cv_path, selectedApplicant.nama)}
                    className="btn-download-full"
                  >
                    <Download size={16} />
                    Download CV
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default JobApp;